import {AfterViewInit, ChangeDetectorRef, Directive, ElementRef, NgZone, OnDestroy} from '@angular/core';
import {AUTOFOCUS_RETRY_MS, canTakeFocus, focusAtEnd, isCovered, isShown, retryMayReclaim} from './autofocus-core';

/**
 * Every control that counts as "a field" when deciding which one comes first —
 * text fields AND the non-text ones (dropdowns, pickers, toggles), because the
 * rule is "focus the first field if it takes typing", not "find some text field".
 */
const FIELD_SELECTOR = [
  'input:not([type="hidden"])', 'textarea', 'select', '[contenteditable=""]', '[contenteditable="true"]',
  'ng-select', 'app-date-picker', 'app-date-range-filter',
  'button[aria-haspopup]', 'button.dd-btn', 'button.fdd-btn',
  // Choice controls built from buttons/divs (segmented "radios", toggles).
  '[role="radio"]', '[role="checkbox"]', '[role="switch"]', '[role="combobox"]',
].join(', ');

/** Text inputs that open a picker rather than taking typing. */
const PICKER_INPUT = '[bsdatepicker], [bsdaterangepicker], [ngbdatepicker]';

/** Input types with a typing caret. */
const CARET_TYPES = new Set(['', 'text', 'search', 'email', 'tel', 'url', 'number', 'password']);

/** How often the open/closed check may run while the form's DOM is churning. */
const CHECK_THROTTLE_MS = 60;

/** While the first field is still covered (a loading veil), how often to look again… */
const PENDING_POLL_MS = 200;
/** …and for how long, at most. The user's first click or key ends the wait sooner. */
const PENDING_LIMIT_MS = 15000;

/**
 * Add/Update forms: when the form opens, put the caret in its first field so
 * typing starts without a click.
 *
 * Attach it to the form's COMPONENT, not a template element:
 *
 *   @Component({ …, hostDirectives: [FormAutofocusDirective] })
 *
 * so the whole template — every root, every `@if` branch — is in scope, and an
 * NgModule component needs no module import.
 *
 * Which field:
 *   - The first VISIBLE field in DOM order. Disabled, read-only and hidden ones
 *     are skipped.
 *   - Only if that field takes typing (text, number, email, textarea, rich text).
 *     When the first field is a dropdown, date picker, checkbox or file picker,
 *     nothing is focused: the form keeps its existing behaviour rather than
 *     getting a caret in some later field.
 *   - `data-no-autofocus` on a field or region removes it from consideration.
 *
 * When — on every OPENING, not once: the form is watched, and focus is placed
 * each time its fields go from absent/hidden to visible. That covers an ngb
 * modal, a route page, an Update form whose fields appear only after its data
 * loads, a panel behind `*ngIf`, and a sidebar that slides in by class change
 * (a field parked off-screen counts as hidden).
 *
 * Only once the field is READY: while a loading veil or backdrop lies over it,
 * a caret would sit where the user can neither see nor click it, so the focus
 * waits for the layer to go (polling briefly). Before treating a veil as real it
 * repaints the form's own view once: Angular 22 compiles components OnPush, so a
 * legacy form that clears `isLoading` inside a `subscribe()` leaves its veil
 * painted until something else marks the view (see loading-state.util.ts — the
 * same `detectChanges()` its `repaintView` does). A veil that survives that is a
 * genuine load, and the wait goes on. The wait ends the moment the user
 * clicks or presses a key anywhere — they have moved on — or after 15s, so focus
 * can never arrive late and land mid-task.
 *
 * Never takes focus away from the user — same rules as the search boxes
 * (`canTakeFocus` in autofocus-core.ts). In particular, a form that already
 * focuses a field itself (`ngbAutofocus`, a ViewChild `.focus()`) wins: that
 * field holds focus, so this does nothing.
 *
 * Same behaviour at every viewport — desktop, tablet and phone. iOS Safari is the
 * one exception it cannot override: WebKit there ignores a focus() that no tap
 * started, so the field is focused but the keyboard waits for the first tap.
 */
@Directive({
  selector: '[appFormAutofocus]',
  standalone: true,
})
export class FormAutofocusDirective implements AfterViewInit, OnDestroy {

  private observer?: MutationObserver;
  private checkTimer?: ReturnType<typeof setTimeout>;
  private retryTimer?: ReturnType<typeof setTimeout>;
  private open = false;
  private focused = false;
  /** Opened with a typing field first, but not focused yet (covered / not ready). */
  private pendingSince = 0;
  private pollTimer?: ReturnType<typeof setTimeout>;
  /** The once-per-opening repaint of a stale view has been done. */
  private repainted = false;
  private readonly onTransitionEnd = () => this.scheduleCheck();
  /** Any click or key ends a pending wait — the user has started doing something. */
  private readonly onUserAction = () => this.endPending();

  /** `cdr` is the HOST form component's: a host directive shares its injector. */
  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      const host = this.el.nativeElement;
      this.observer = new MutationObserver(() => this.scheduleCheck());
      this.observer.observe(host, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'disabled'],
      });
      host.addEventListener('transitionend', this.onTransitionEnd);
      document.addEventListener('pointerdown', this.onUserAction, true);
      document.addEventListener('keydown', this.onUserAction, true);
      this.scheduleCheck();
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.el.nativeElement.removeEventListener('transitionend', this.onTransitionEnd);
    document.removeEventListener('pointerdown', this.onUserAction, true);
    document.removeEventListener('keydown', this.onUserAction, true);
    clearTimeout(this.checkTimer);
    clearTimeout(this.retryTimer);
    clearTimeout(this.pollTimer);
  }

  private scheduleCheck(): void {
    if (this.checkTimer === undefined) {
      this.checkTimer = setTimeout(() => {
        this.checkTimer = undefined;
        this.check();
      }, this.open ? CHECK_THROTTLE_MS : 0);
    }
  }

  /** Detects the closed → open edge, then focuses once per opening when the field is ready. */
  private check(): void {
    const field = this.firstField();
    if (!field) {
      this.open = false;
      this.endPending();
      return;
    }
    if (!this.open) {
      this.open = true;
      this.focused = false;
      if (!takesTyping(field)) {
        return;
      }
      this.pendingSince = Date.now();
      this.repainted = false;
    }
    if (this.pendingSince) {
      this.attempt(field);
    }
  }

  /** One focus attempt for the current opening; keeps waiting only while the field is covered. */
  private attempt(field: HTMLElement): void {
    const active = document.activeElement;
    if (active === field || !takesTyping(field) || !canTakeFocus(field, active)) {
      this.endPending();   // already there, no longer a typing field, or the user is busy elsewhere
      return;
    }
    if (isCovered(field) && !this.repainted) {
      this.repainted = true;
      try { this.cdr.detectChanges(); } catch { /* view torn down mid-check */ }
    }
    if (isCovered(field)) {
      if (Date.now() - this.pendingSince > PENDING_LIMIT_MS) {
        this.endPending();
      } else {
        clearTimeout(this.pollTimer);
        this.pollTimer = setTimeout(() => this.scheduleCheck(), PENDING_POLL_MS);
      }
      return;
    }
    this.endPending();
    this.focused = focusAtEnd(field, this.isPage());
    // ng-bootstrap can still move focus to its window after this; reclaim it once.
    clearTimeout(this.retryTimer);
    this.retryTimer = setTimeout(() => {
      const again = this.firstField();
      const now = document.activeElement;
      if (again && takesTyping(again) && now !== again && canTakeFocus(again, now) &&
          retryMayReclaim(again, now, this.focused) && !isCovered(again)) {
        this.focused = focusAtEnd(again, this.isPage());
      }
    }, AUTOFOCUS_RETRY_MS);
  }

  private endPending(): void {
    this.pendingSince = 0;
    clearTimeout(this.pollTimer);
  }

  /** First visible, enabled field in DOM order — of any kind. */
  private firstField(): HTMLElement | null {
    const all = this.el.nativeElement.querySelectorAll<HTMLElement>(FIELD_SELECTOR);
    for (const el of Array.from(all)) {
      if (el.closest('[data-no-autofocus]')) {
        continue;
      }
      // An ng-select / date-picker counts once, as itself — not via its inner input or button.
      if (el.parentElement?.closest('ng-select, app-date-picker, app-date-range-filter')) {
        continue;
      }
      if ((el as HTMLInputElement).disabled || (el as HTMLInputElement).readOnly || !isShown(el) || !inViewport(el)) {
        continue;
      }
      return el;
    }
    return null;
  }

  /** A routed page (sits right after its <router-outlet>), as opposed to a dialog or panel. */
  private isPage(): boolean {
    const host = this.el.nativeElement;
    return host.previousElementSibling?.tagName === 'ROUTER-OUTLET' && !host.closest('ngb-modal-window');
  }
}

function takesTyping(el: HTMLElement): boolean {
  if (el.isContentEditable || el instanceof HTMLTextAreaElement) {
    return true;
  }
  return el instanceof HTMLInputElement && CARET_TYPES.has(el.type) && !el.matches(PICKER_INPUT);
}

/**
 * Parked off-screen to the side = a closed slide-in panel; it is not open yet.
 * Horizontal only: a page whose first field sits below the fold is still open,
 * and must not "open" later, mid-scroll, when the user brings it into view.
 */
function inViewport(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  return r.right > 0 && r.left < window.innerWidth;
}
